import React, { useState, useEffect, lazy, Suspense } from "react";
import * as XLSX from "xlsx";
import moment from "moment";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import { toast } from "react-toastify";
import {
  BsBarChartLine,
  BsDownload,
  BsPeople,
  BsFileEarmarkSpreadsheet,
  BsExclamationTriangleFill,
} from "react-icons/bs";

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";

import { useSelector } from "react-redux";

// UI Components from Design System
import {
  DrawerContainer,
  AnalyticsNavigation,
  Banner,
  BannerContent,
  BannerTitle,
  BannerDescription,
  BannerImage,
  gradients,
  SoftButton,
  PrimaryButton,
} from "../../../components/ui";

import estadisticasCharacter from "../../../assets/characters-banners/estadisticas.png";

import AnalyticsCard from "../../../components/AnalyticsCard/AnalyticsCard.component";
import AnalyticsTable from "../../../components/analyticsTable/AnalyticsTable.component";
import {
  ContentWrapper,
  KPIGrid,
  FilterToolbar,
  FilterGroup,
  FilterLabel,
  DatePickerWrapper,
  DateArrow,
  ButtonGroup,
  LoaderContainer,
  WarningBanner,
} from "./AnalyticsView.styles";

import { getAnalytics, getCustomFieldsFromBot } from "../../../utils/analytics";
import { getAgentsBySpaceId } from "../../../utils/getAgents";
import LoaderComponent from "../../../components/loader/Loader.component";
import CONSTANTS from "../../../const/constants";
import wave1 from "../../../assets/images/cards/wave1.png";
import wave2 from "../../../assets/images/cards/wave2.png";
import wave3 from "../../../assets/images/cards/wave3.png";
import wave4 from "../../../assets/images/cards/wave4.png";
import { groupBy } from "lodash";
import AdvancedAnalyticsDashboard from "./AdvancedAnalyticsDashboard.component";

import { canSeeTemplateAnalysis } from "../TemplateAnalysisView/access";

// Carga diferida: el chunk del panel solo se pide para quien puede verlo.
const TemplateAnalysisPanel = lazy(() =>
  import("../TemplateAnalysisView/TemplateAnalysisPanel")
);

const AnalyticsView = () => {
  const userData = useSelector((state) => state.user.data);
  const selectedSpace = useSelector((state) => state.spaces.selected);
  const companyName = useSelector((state) => state.user.data.company?.name);
  const companyId = useSelector((state) => state.user.data.company?.id);

  // Determinar si se debe filtrar por espacio seleccionado
  const shouldFilterBySelectedSpace = companyId === CONSTANTS.SPACE_FILTERED_COMPANY_ID;

  // Análisis de plantillas: misma lógica de aparición que el nav principal.
  const canSeeTemplateAnalysisPanel = canSeeTemplateAnalysis(userData);

  // Vista inicial: nuevo panel si el feature flag está activo; panel clásico en caso contrario.
  const [activeView, setActiveView] = useState('analiticasAvanzadas');
  const [spaceUserIds, setSpaceUserIds] = useState([]);

  const firstEndDate = moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss");
  const firstInitialDate = moment()
    .startOf("day")
    .subtract(1, "weeks")
    .format("YYYY-MM-DDTHH:mm:ss");
  const today = moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss");

  const [initalDate, setInitalDate] = useState(firstInitialDate);
  const [endDate, setEndDate] = useState(firstEndDate);

  const [isLoading, setIsLoading] = useState(true);

  const [messages, setMessages] = useState(0);
  const [activeClients, setActiveClients] = useState(0);
  const [timeWithMoreMessages, setTimeWithMoreMessages] = useState(0);
  const [whatsapp, setWhatsapp] = useState(0);
  const [messenger, setMessenger] = useState(0);
  const [answeredChats, setAnsweredChats] = useState(0);
  const [unansweredChats, setUnansweredChats] = useState(0);
  const [agentsData, setAgentsData] = useState([]);
  const [totalFromAds, setTotalFromAds] = useState([]);
  const [clients, setClients] = useState([]);
  const [averageTimeAfterAssignation, setAverageTimeAfterAssignation] = useState(0);
  const [averageServiceTime, setAverageServiceTime] = useState(0);
  const [showDataLimitWarning, setShowDataLimitWarning] = useState(false);

  // Check if user can see bot reports
  const canSeeBotReports = companyId === CONSTANTS.AGUASAFE_COMPANY_ID ||
    companyId === CONSTANTS.IVENTAS_COMPANY_ID ||
    companyId === CONSTANTS.ESSBIO_COMPANY_ID ||
    companyId === CONSTANTS.BIODIVERSA_COMPANY_ID ||
    companyId === "663a62cb24aa9a0008f52d08";

  const setData = async (selectedInitDate, selectedEndDate) => {
    try {
      console.log("SELECTED SPACE", selectedSpace);
      console.log("COMPANY NAME", companyName);
      setIsLoading(true);
      
      // Obtener usuarios del espacio seleccionado para la compañía específica
      let spaceUsers = [];
      if (shouldFilterBySelectedSpace && selectedSpace?.id) {
        try {
          spaceUsers = await getAgentsBySpaceId(selectedSpace.id);
          setSpaceUserIds(spaceUsers?.map(u => u.id) || []);
        } catch (error) {
          console.error("Error fetching space users:", error);
        }
      }
      
      // Determinar canales a filtrar
      const channelsToFilter = 
        (selectedSpace?.channels?.length > 0 && 
          (companyName === CONSTANTS.VINTE_COMPANY_NAME || shouldFilterBySelectedSpace))
          ? selectedSpace?.channels?.map((x) => x.id)
          : [];
      
      getAnalytics(
        selectedInitDate,
        selectedEndDate,
        channelsToFilter
      ).then((response) => {
        setTimeWithMoreMessages(response.timeWithMoreMessages);
        setWhatsapp(response.whatsapp);
        setMessenger(response.messenger);
        setAnsweredChats(response.totalAnswered);
        setUnansweredChats(response.totalUnanswered);
        let mappedAgentsData = response.agents.map(a => { return {
          ...a,
          ...(CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId)
            && { timeAfterAssignation:
              Math.round((((response?.botStats[a?.id]?.timeAfterAssignation ?? 0)))
               / (response?.botStats[a?.id]?.countAfterAssignation ?? 1)) }),
          ...(CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId)
            && { answeredChatsBot: response?.botStats[a?.id]?.countAfterAssignation ?? 0 }),
          ...(CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId)
            && { averageServiceTime: (() => {
              const sessions = response?.botStats[a?.id]?.sessions;
              if (!sessions || !Array.isArray(sessions) || sessions.length < 2) return 0;

              const sortedSessions = sessions.sort((a, b) => new Date(a) - new Date(b));
              let totalDifference = 0;

              for (let i = 1; i < sortedSessions.length; i++) {
                const current = new Date(sortedSessions[i]);
                const previous = new Date(sortedSessions[i - 1]);
                const diffInMinutes = (current - previous) / (1000 * 60);
                totalDifference += diffInMinutes;
              }

              return Math.round(((totalDifference / (sortedSessions.length - 1)) / 60) * 100) / 100;
            })() })
        }});
        
        // Filtrar agentes por espacio seleccionado para la compañía específica
        if (shouldFilterBySelectedSpace && spaceUsers.length > 0) {
          const spaceUserIdsSet = new Set(spaceUsers.map(u => u.id));
          mappedAgentsData = mappedAgentsData.filter(agent => spaceUserIdsSet.has(agent.id));
        }
        
        setAgentsData(mappedAgentsData);
        if (CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId)) {
          const totalTimeAfterAssignation = mappedAgentsData.reduce((acc, curr) => acc + (curr.timeAfterAssignation || 0), 0);
          const totalServiceTime = mappedAgentsData.reduce((acc, curr) => acc + (curr.averageServiceTime || 0), 0);
          setAverageTimeAfterAssignation(mappedAgentsData.length > 0 ? Math.round((totalTimeAfterAssignation / mappedAgentsData.length) * 100) / 100 : 0);
          setAverageServiceTime(mappedAgentsData.length > 0 ? Math.round((totalServiceTime / mappedAgentsData.length) * 100) / 100 : 0);
        }
        setMessages(response.totalMessages);
        setActiveClients(response.totalActive);
        setTotalFromAds(response.totalFromAds);
        setIsLoading(false);
        setClients(response.clients);
        setShowDataLimitWarning(!CONSTANTS.STATS_LIMITS_ALLOWED[companyId]);
      }).catch((error) => {
        setIsLoading(false);
        toast.warn(
          "Error al obtener los datos de estadísticas, por favor comuníquese con soporte.",
          {
          position: "top-center",
          autoClose: 15000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          }
        );
      });
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setData(firstInitialDate, firstEndDate);
  }, []);

  // Recargar datos cuando cambie el espacio seleccionado (solo para la compañía específica)
  useEffect(() => {
    if (shouldFilterBySelectedSpace && selectedSpace?.id) {
      setData(initalDate, endDate);
    }
  }, [selectedSpace?.id]);

  const handleInitialDate = (date) => {
    const initDate = moment(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
    setInitalDate(initDate);
    let _endDate = endDate;
    if (moment(endDate).isAfter(moment(date).add(CONSTANTS.STATS_LIMITS_ALLOWED[companyId] ? CONSTANTS.STATS_LIMITS_ALLOWED[companyId] : 15, "days"))) {
      setEndDate(moment(date).endOf("day").add(CONSTANTS.STATS_LIMITS_ALLOWED[companyId] ? CONSTANTS.STATS_LIMITS_ALLOWED[companyId] : 15, "days"));
      _endDate = moment(date).endOf("day").add(CONSTANTS.STATS_LIMITS_ALLOWED[companyId] ? CONSTANTS.STATS_LIMITS_ALLOWED[companyId] : 15, "days");
    }
    setIsLoading(true);
    setData(initDate, _endDate);
  };

  const handleEndDate = (date) => {
    const finalDate = moment(date).endOf("day").format("YYYY-MM-DDTHH:mm:ss");
    setEndDate(finalDate);
    let _initialDate = initalDate;
    if (moment(initalDate).isBefore(moment(date).subtract(CONSTANTS.STATS_LIMITS_ALLOWED[companyId] ? CONSTANTS.STATS_LIMITS_ALLOWED[companyId] : 15, "days"))) {
      setInitalDate(moment(date).startOf("day").subtract(CONSTANTS.STATS_LIMITS_ALLOWED[companyId] ? CONSTANTS.STATS_LIMITS_ALLOWED[companyId] : 15, "days"));
      _initialDate = moment(date).startOf("day").subtract(CONSTANTS.STATS_LIMITS_ALLOWED[companyId] ? CONSTANTS.STATS_LIMITS_ALLOWED[companyId] : 15, "days");
    }
    setIsLoading(true);
    setData(_initialDate, finalDate);
  };

  const gotToAgents = () => {
    window.location.href = "/analytics/agents";
  };

  const getMessageStatus = (messageStatus) =>
    CONSTANTS.MESSAGE_STATUS[messageStatus?.toUpperCase()] || "";

  const exportAnalytics = async () => {
    const workbook = XLSX.utils.book_new();

    const clientsData = [];
    const clientsHeaders = [
      "Nombre",
      "Teléfono",
      "Canal",
      "Agente",
      "Etiquetas",
      "Plataforma",
      "Fecha de creación",
      "Ultima actualización",
      "Status del último mensaje"
    ];

    clientsData.push(clientsHeaders);

    clients.forEach((x) => {
      const lastMessage = x.messages.filter((x) => x.sender !== "CLIENT").pop();
      const clientRow = [
        x?.nameFromChannel,
        userData?.permissions?.canSeePhoneNumber ? x?.senderId : "",
        x?.channel?.name || x?.channel?.phone || "",
        x?.agent?.name || "",
        x?.tags?.map((x) => x?.name).join(", "),
        x?.channel?.platform?.name || "",
        moment(x?.createdAt).format("DD/MM/YYYY HH:mm:ss"),
        moment(lastMessage?.updatedAt).format("DD/MM/YYYY HH:mm:ss"),
        getMessageStatus(lastMessage?.messageStatus) || ""
      ];
      clientsData.push(clientRow);
    });

    const agentsSheetData = [];
    const agentsHeaders = ["Nombre", "Mensajes", "Chats"];

    if (CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId)) {
      agentsHeaders.push("Tiempo después de asignación (min)", "Chats respondidos por bot", "Tiempo promedio de servicio (hrs)");
    }

    agentsSheetData.push(agentsHeaders);

    agentsData.forEach((agent) => {
      const agentRow = [
        agent?.name || "",
        agent?.totalMessages || 0,
        agent?.answeredChats || 0
      ];

      if (CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId)) {
        agentRow.push(
          agent?.timeAfterAssignation || 0,
          agent?.answeredChatsBot || 0,
          agent?.averageServiceTime || 0
        );
      }

      agentsSheetData.push(agentRow);
    });

    const clientsWorksheet = XLSX.utils.aoa_to_sheet(clientsData);
    const agentsWorksheet = XLSX.utils.aoa_to_sheet(agentsSheetData);

    XLSX.utils.book_append_sheet(workbook, clientsWorksheet, "Clientes");
    XLSX.utils.book_append_sheet(workbook, agentsWorksheet, "Agentes");

    XLSX.writeFileXLSX(workbook, "iventas.xlsx");
  };

  const headerConfigs = {
    [CONSTANTS.AGUASAFE_COMPANY_ID]: [
      "N° Correlativo",
      "Fecha de Registro",
      "Hora de Registro",
      "N° Télefono Usuario",
      "Tipo de evento",
      "Nombre",
      "RUT",
      "Fecha inc.",
      "Region",
      "Lugar o intalación",
      "Empresa",
      "Detalles",
      "Evidencia 1",
      "Evidencia 2",
      "Evidencia 3",
      "Evidencia 4",
      "Evidencia 5",
      "Detalle Cierre",
    ],
    ["663a62cb24aa9a0008f52d08"]: [
      "N° Correlativo",
      "Fecha de Registro",
      "Hora de Registro",
      "N° Télefono Usuario",
      "Tipo de evento",
      "Nombre",
      "RUT",
      "Fecha inc.",
      "Region",
      "Lugar o intalación",
      "Empresa",
      "Detalles",
      "Evidencia 1",
      "Evidencia 2",
      "Evidencia 3",
    ],
    [CONSTANTS.ESSBIO_COMPANY_ID]: [
      "Puesto",
      "Nombre",
      "Región de la reunión",
      "Comuna",
      "Cargo",
      "Nombre de la persona con quien se reunió",
      "Fecha de la reunión",
      "Lugar de la reunión",
      "Evaluación de la reunión",
      "Grupos de interés o Stakeholder",
      "Otras organizaciones 1",
      "Otras organizaciones 2",
      "Otras organizaciones 3",
      "Resumen de la reunión",
      "Objetivo de la reunión",
      "Evidencia 1",
      "Evidencia 2",
      "Evidencia 3",
    ],
    [CONSTANTS.BIODIVERSA_COMPANY_ID]: [
      "Tipo de evento",
      "Nombre",
      "Empresa Contratista",
      "Cargo",
      "Fecha inc.",
      "Region",
      "Lugar o intalación",
      "Empresa",
      "Detalles",
      "Evidencia 1",
      "Evidencia 2",
      "Evidencia 3",
    ],
  };

  const handleExportReportBot = async () => {
    const headers = headerConfigs[companyId];
    if (headers) {
      await exportReportBot(headers);
    } else {
      console.error("Invalid companyId or missing header configuration");
      toast.error("No se pudo generar el reporte del bot", {
        position: "top-center",
        autoClose: 5000,
      });
    }
  };

  const exportReportBot = async (headers) => {
    setIsLoading(true);
    try {
      const getCustomFieldFromBot = await getCustomFieldsFromBot(
        initalDate,
        endDate,
        companyName,
        selectedSpace?.channels?.map((x) => x.id)
      );
      console.log("getCustomFieldFromBot", getCustomFieldFromBot);

      const resultMatrix = [];
      resultMatrix.push(headers);

      getCustomFieldFromBot.forEach((clientInfo) => {
        const clientInfoByFlowId = groupBy(clientInfo, "flowId");

        Object.keys(clientInfoByFlowId).forEach((flowId) => {
          const clientInfo = clientInfoByFlowId[flowId];
          const newRow = initializeNewRow(clientInfo, headers);
          mapFieldsToRow(clientInfo, newRow, headers);
          resultMatrix.push(Object.values(newRow));
        });
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(resultMatrix);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte Bot");
      XLSX.writeFileXLSX(workbook, "reporteBot.xlsx");
      setIsLoading(false);
    } catch (error) {
      console.error("Error exporting bot report:", error);
      setIsLoading(false);
      toast.error("Error al exportar el reporte del bot", {
        position: "top-center",
        autoClose: 5000,
      });
    }
  };

  function initializeNewRow(clientInfo, headers) {
    let newRow = {};
    headers.forEach((header) => {
      newRow[header] = "";
    });
    if (
      CONSTANTS.AGUASAFE_COMPANY_ID === companyId ||
      "663a62cb24aa9a0008f52d08" === companyId
    ) {
      newRow["N° Correlativo"] = clientInfo[0]?.flowId || "";
      newRow["Fecha de Registro"] =
        moment(clientInfo[0].createdAtClient).format("DD/MM/YYYY") || "";
      newRow["Hora de Registro"] = moment(clientInfo[0].createdAtClient).format(
        "HH:mm:ss"
      );
      newRow["N° Télefono Usuario"] = clientInfo[0]?.senderId || "";

      clientInfo.forEach((element) => {
        const fieldName = element?.fieldName?.split("_")[1];
        if (fieldName && newRow.hasOwnProperty(fieldName)) {
          newRow[fieldName] = element.value || "";
        } else if (fieldName === "Tipo de accidente") {
          newRow["Tipo de evento"] = element.value || "";
        } else if (fieldName === "Fecha del accidente") {
          newRow["Fecha inc."] = element.value || "";
        } else if (fieldName === "Lugar del accidente") {
          newRow["Lugar o intalación"] = element.value || "";
        }
      });
    }
    return newRow;
  }

  function mapFieldsToRow(clientInfo, newRow, headers) {
    clientInfo.forEach((element) => {
      const fieldName = element?.fieldName.replace("BOT_", "");

      const mappedHeader =
        headers.find((header) => header.includes(`${fieldName}`)) || fieldName;

      if (mappedHeader && newRow.hasOwnProperty(mappedHeader)) {
        newRow[mappedHeader] = element.value || "";
      }
    });
  }

  return (
    <DrawerContainer>
      {/* Left Navigation */}
      <AnalyticsNavigation
        activeView={activeView}
        onViewChange={setActiveView}
        showTemplateAnalysis={canSeeTemplateAnalysisPanel}
      />

      {/* Main Content */}
      <ContentWrapper data-analytics-content>
        {activeView === "analiticasAvanzadas" && (
          <AdvancedAnalyticsDashboard
            companyId={companyId}
            selectedSpace={selectedSpace}
            companyName={companyName}
            showTemplateAnalysis={canSeeTemplateAnalysisPanel}
            onNavigateToTemplates={() => setActiveView("plantillas")}
          />
        )}

        {activeView === "estadisticas" && (
          <>
        {/* Indigo Gradient Banner */}
        <Banner gradient={gradients.indigo}>
          <BannerContent>
            <BannerTitle>Panel de Estadísticas</BannerTitle>
            <BannerDescription>
              Analiza el rendimiento de tu equipo y conversaciones
            </BannerDescription>
          </BannerContent>
          <BannerImage src={estadisticasCharacter} alt="Estadísticas" />
        </Banner>

        {/* Data Limit Warning */}
        {showDataLimitWarning && (
          <WarningBanner>
            <BsExclamationTriangleFill />
            Los datos de estadísticas están limitados a 500 registros. Si necesitas el reporte completo, solicítalo a soporte.
          </WarningBanner>
        )}

        {/* Filter Toolbar */}
        <FilterToolbar>
          <FilterGroup>
            <FilterLabel>Periodo:</FilterLabel>
            <DatePickerWrapper>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  autoOk
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="date-picker-init"
                  value={initalDate}
                  maxDate={endDate}
                  onChange={handleInitialDate}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
                <DateArrow>→</DateArrow>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  autoOk
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="date-picker-end"
                  value={endDate}
                  minDate={initalDate}
                  maxDate={today}
                  onChange={handleEndDate}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
              </MuiPickersUtilsProvider>
            </DatePickerWrapper>
          </FilterGroup>

          <ButtonGroup>
            <SoftButton onClick={gotToAgents} disabled={isLoading}>
              <BsPeople />
              {userData.rol.name === "admin" || userData.rol.name === "superadmin"
                ? "Agentes"
                : "Agente"}
            </SoftButton>

            <PrimaryButton onClick={exportAnalytics} disabled={isLoading}>
              <BsDownload />
              Exportar
            </PrimaryButton>

            {canSeeBotReports && (
              <SoftButton onClick={handleExportReportBot} disabled={isLoading}>
                <BsFileEarmarkSpreadsheet />
                Reportes del Bot
              </SoftButton>
            )}
          </ButtonGroup>
        </FilterToolbar>

        {isLoading ? (
          <LoaderContainer>
            <LoaderComponent width={50} height={50} />
          </LoaderContainer>
        ) : (
          <>
            {/* KPI Cards Section */}
            <section id="kpis-section">
              <KPIGrid>
                <AnalyticsCard
                  color="#4F46E5"
                  title="Conversaciones"
                  image={wave4}
                  count={messages?.toLocaleString("en-US") || 0}
                />
                <AnalyticsCard
                  color="#4F46E5"
                  title="Conversaciones Activas"
                  image={wave4}
                  count={activeClients?.toLocaleString("en-US") || 0}
                />
                <AnalyticsCard
                  color="#059669"
                  title="WhatsApps"
                  image={wave3}
                  count={whatsapp?.toLocaleString("en-US") || 0}
                />
                <AnalyticsCard
                  color="#3B82F6"
                  title="Facebook Messenger"
                  count={messenger?.toLocaleString("en-US") || 0}
                  image={wave1}
                />
                <AnalyticsCard
                  color="#3B82F6"
                  title="Campañas de anuncios"
                  count={totalFromAds?.toLocaleString("en-US") || 0}
                  image={wave1}
                />
                <AnalyticsCard
                  color="#059669"
                  title="Chats respondidos"
                  count={answeredChats?.toLocaleString("en-US") || 0}
                  image={wave1}
                />
                <AnalyticsCard
                  color="#F59E0B"
                  title="Chats sin responder"
                  count={unansweredChats?.toLocaleString("en-US") || 0}
                  image={wave2}
                />
                <AnalyticsCard
                  color="#6366F1"
                  title="Horario con más mensajes"
                  count={
                    timeWithMoreMessages === ""
                      ? "Sin mensajes"
                      : moment(timeWithMoreMessages).format("hh A")
                  }
                  image={wave4}
                  hideSubtitle
                />
                {CONSTANTS.STATS_BOTS_ACTIVE.includes(companyId) && (
                  <>
                    <AnalyticsCard
                      color="#6366F1"
                      title="Promedio respuesta después de asignación (min)"
                      count={averageTimeAfterAssignation}
                      image={wave2}
                    />
                    <AnalyticsCard
                      color="#6366F1"
                      title="Promedio duración de conversación (hrs)"
                      count={averageServiceTime}
                      image={wave4}
                    />
                  </>
                )}
              </KPIGrid>
            </section>

            {/* Agents Table Section */}
            <section id="agents-section">
              <AnalyticsTable data={agentsData} />
            </section>
          </>
        )}
        </>
        )}

        {activeView === "plantillas" && (
          /* Análisis de plantillas nativo, montado dentro de la sección sin cambiar de ruta */
          <Suspense
            fallback={
              <LoaderContainer>
                <LoaderComponent width={50} height={50} />
              </LoaderContainer>
            }
          >
            <TemplateAnalysisPanel />
          </Suspense>
        )}
      </ContentWrapper>
    </DrawerContainer>
  );
};

export default AnalyticsView;
